import { useState, useRef } from "react";
import { apiRequest } from "../services/api_services/ApiServices";
import { toast } from "react-toastify";

const apiUrl = process.env.REACT_APP_API_URL;

const usePrechat = () => {
  const [loading, setLoading] = useState(false);
  const [answerLoading, setAnswerLoading] = useState(false);
  const [convoList, setConvoList] = useState([]);
  const [chatId, setChatId] = useState(0);
  const [conversationId, setConversationId] = useState(-1);
  const [sessionId, setSessionId] = useState("");
  const [chatConversation, setChatConversation] = useState([]);
  const [application, setApplication] = useState("");
  const [options, setOptions] = useState([]);
  const [isNewAnswer, setIsNewAnswer] = useState(false);
  const [newChat, setNewChat] = useState("");
  const [skip, setSkip] = useState(0);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [conversationListLoading, setConversationListLoading] = useState(false);
  const [chatDataLoading, setChatDataLoading] = useState(false);
  const endRef = useRef(null);
  const lastFetchedIdRef = useRef(null);
  const isFetchingAppListRef = useRef(false);
  const isFetchingConversationRef = useRef(false);

  const scrollToBottom = () => {
    setTimeout(() => {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 500);
  };

  const getConversation = async (appId) => {
    if (isFetchingConversationRef.current) return;
    isFetchingConversationRef.current = true;
    setConversationListLoading(true);
    setSkip(0); // Reset skip on initial fetch
    setConvoList([]); // Clear list for initial fetch
    try {
      const response = await apiRequest({
        method: "GET",
        url: "conversation?search=&limit=20&skip=0",
      });
      if (response?.success === true) {
        setConvoList(response.convos || []);
        // Check if there are more conversations to load
        setHasMoreConversations(response.convos?.length >= 20);
      }
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
      isFetchingConversationRef.current = false;
      setConversationListLoading(false);
    }
  };

  const loadMoreConversations = async (appId) => {
    if (isLoadingMore || !hasMoreConversations) return;
    setIsLoadingMore(true);
    const nextSkip = skip + 20;
    try {
      const response = await apiRequest({
        method: "GET",
        url: `conversation?search=&limit=20&skip=${nextSkip}`,
      });
      if (response?.success === true) {
        const newConvos = response.convos || [];
        setConvoList((prevList) => [...prevList, ...newConvos]);
        setSkip(nextSkip);
        // Check if more conversations exist
        setHasMoreConversations(newConvos.length >= 20);
      }
    } catch (err) {
      console.error("Error loading more conversations:", err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const getChat = async (id, currentApp) => {
    if (id === 0) {
      setChatId(id);
      setSessionId("");
      setChatConversation([]);
      setConversationId(0);
      localStorage.removeItem("conversationId");
      lastFetchedIdRef.current = null;
      return;
    }

    if (lastFetchedIdRef.current === id) {
      return;
    }
    lastFetchedIdRef.current = id;

    setChatId(id);
    setSessionId("");
    setIsNewAnswer(false);
    setChatDataLoading(true);
    try {
      const response = await apiRequest({
        method: "GET",
        url: apiUrl + "conversation/" + id,
      });

      if (response?.success === true) {
        const rawChats = response.convo?.chats || [];
        // Normalize API chats into the same shape as live messages.
        // Each API entry may contain: question, base64, answer, data, date.
        // Split into user-bubble + bot-bubble entries so MessageList renders
        // them identically to freshly sent messages.
        const normalizedChats = [];
        rawChats.forEach((chat) => {
          const dateVal = chat.date || chat.dateAt || null;
          // User bubble — only add if there is a question or an image
          if (chat.question || chat.base64) {
            const userEntry = { dateAt: dateVal };
            if (chat.question) userEntry.question = chat.question;
            if (chat.base64) userEntry.questionImage = chat.base64;
            normalizedChats.push(userEntry);
          }
          // Bot bubble — only add if there is an answer or table data
          if (chat.answer || (chat.data && chat.data.length > 0)) {
            normalizedChats.push({
              answer: chat.answer || "",
              data: chat.data || null,
              dateAt: dateVal,
            });
          }
        });
        setChatConversation(normalizedChats);
        if (response.convo?.session_id) {
          setSessionId(response.convo.session_id);
        }
      }
    } catch (error) {
      console.error("API error occurred", error);
      if (lastFetchedIdRef.current === id) {
        lastFetchedIdRef.current = null;
      }
    } finally {
      setChatDataLoading(false);
    }
  };

  const applicationList = async () => {
    if (isFetchingAppListRef.current) return;
    isFetchingAppListRef.current = true;
    try {
      const response = await apiRequest({
        method: "GET",
        url: "app_list",
      });

      if (response?.success) {
        const applications = response.data?.data ?? [];
        const formattedOptions = applications.map((app) => ({
          value: app.id,
          label: app.name,
          icon: app.icon,
          status: app.status,
        }));
        setOptions(formattedOptions);

        if (formattedOptions.length > 0) {
          setApplication(formattedOptions[0].value);
          getConversation(formattedOptions[0].value);
        }
      }
    } catch (error) {
      console.error("Application list error:", error);
    } finally {
      isFetchingAppListRef.current = false;
    }
  };

  const sendMessage = async (
    question,
    tempConversation,
    currentApp,
    base64Image,
    location,
  ) => {
    setAnswerLoading(true);
    try {
      let activeSessionId = sessionId;
      if (!activeSessionId) {
        if (conversationId && conversationId > 0) {
          const convo = convoList?.find((c) => c.id === conversationId);
          activeSessionId = convo?.session_id || String(conversationId);
        } else {
          const maxConvoId =
            convoList && convoList.length > 0
              ? Math.max(...convoList.map((c) => Number(c.id) || 0))
              : 0;
          activeSessionId = String(maxConvoId + 1);
        }
        setSessionId(activeSessionId);
      }

      const payload = {
        question: question,
        session_id: activeSessionId,
        convo_id: conversationId > 0 ? activeSessionId : "",
      };

      if (base64Image) {
        payload.base64 = base64Image;
      }
      if (location !== undefined) {
        payload.location = location;
      }

      const response = await apiRequest({
        method: "Post",
        url: "prechat",
        data: payload,
      });

      if (response?.success === true || response?.status === "success") {
        const newConversationId =
          response.id || Number(activeSessionId) || conversationId;
        setConversationId(newConversationId);
        localStorage.setItem("conversationId", newConversationId);
        setIsNewAnswer(true);

        const answerText =
          response.answer?.nlp_response ||
          response.nlp_response ||
          (typeof response.answer === "string" ? response.answer : "");

        const responseData = response.answer?.data || response.data || null;

        tempConversation.push({
          answer: answerText,
          data: responseData,
          dateAt: new Date(),
        });
        setChatConversation(tempConversation);
        getConversation(currentApp || application);
        setNewChat("Add Convon Id");
        scrollToBottom();

        return {
          success: true,
          openCamera: !!response.answer?.camera,
        };
      }
    } catch (e) {
      console.error(e);
    } finally {
      setAnswerLoading(false);
    }
    return { success: false, openCamera: false };
  };

  return {
    loading,
    setLoading,
    answerLoading,
    convoList,
    chatId,
    setChatId,
    conversationId,
    setConversationId,
    sessionId,
    setSessionId,
    chatConversation,
    setChatConversation,
    application,
    setApplication,
    options,
    isNewAnswer,
    newChat,
    setNewChat,
    endRef,
    scrollToBottom,
    getConversation,
    getChat,
    applicationList,
    sendMessage,
    loadMoreConversations,
    hasMoreConversations,
    isLoadingMore,
    conversationListLoading,
    chatDataLoading,
  };
};

export default usePrechat;
